import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Barcode as BarcodeIcon, CheckCircle, AlertCircle as AlertIcon, Clock, Loader, Activity } from 'lucide-react';
import { recordService } from '../../services/api';

const ServiceEngineerDashboard = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState({
    pending: [],
    hold: [],
    loading: true,
    error: null,
  });

  const [stats, setStats] = useState({
    pendingCount: 0,
    assistedCount: 0,
    holdCount: 0,
    returnCount: 0,
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const [allRecordsResponse, recordStatsResponse] = await Promise.all([
        recordService.getAll({ limit: 100 }),
        recordService.getStatistics(),
      ]);

      // API returns: { success, message, data: { records: [...], limit, page } }
      const allRecordsData = allRecordsResponse.data?.data?.records || [];
      const statsData = recordStatsResponse.data?.data || {};

      const pending = allRecordsData.filter(r => r.status === 'PENDING') || [];
      const hold = allRecordsData.filter(r => r.status === 'HOLD') || [];

      setRecords({
        pending,
        hold,
        loading: false,
        error: null,
      });

      setStats({
        pendingCount: statsData.pending || 0,
        assistedCount: statsData.assisted || 0,
        holdCount: statsData.hold || 0,
        returnCount: statsData.return || 0,
      });
    } catch (err) {
      console.error('Dashboard error:', err);
      setRecords((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load records',
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ASSISTED':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-300';
      case 'HOLD':
        return 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-300';
      case 'RETURN':
        return 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-300';
      case 'PENDING':
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-300';
      default:
        return 'bg-slate-50 text-slate-700 border border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ASSISTED':
        return <CheckCircle className="w-4 h-4" />;
      case 'HOLD':
        return <Clock className="w-4 h-4" />;
      case 'RETURN':
        return <AlertIcon className="w-4 h-4" />;
      case 'PENDING':
        return <BarcodeIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ASSISTED':
        return 'from-green-500 to-emerald-600';
      case 'HOLD':
        return 'from-amber-500 to-orange-600';
      case 'RETURN':
        return 'from-red-500 to-rose-600';
      case 'PENDING':
        return 'from-blue-500 to-indigo-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  if (records.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-4">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-slate-700 font-semibold">Loading your records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="mb-2">
        <h2 className="text-5xl font-bold text-slate-950 tracking-tight">Service Engineer Dashboard</h2>
        <p className="text-slate-500 mt-3 text-base font-medium">Process pending records and track completion status</p>
      </div>

      {/* Error Message */}
      {records.error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-xl flex items-center gap-3 shadow-sm">
          <AlertIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-900">{records.error}</p>
        </div>
      )}

      {/* Quick Stats - Only show Pending and Hold */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { label: 'Pending', value: stats.pendingCount, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50', icon: BarcodeIcon },
          { label: 'On Hold', value: stats.holdCount, color: 'from-amber-500 to-orange-600', bgColor: 'bg-amber-50', icon: Clock },
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div
              key={idx}
              className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition transform hover:scale-105"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-semibold uppercase tracking-wide">{stat.label}</p>
                  <p className="text-4xl font-bold text-slate-900 mt-3">{stat.value}</p>
                </div>
                <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending Records */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 hover:shadow-md transition">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Pending Records</h3>
            <p className="text-slate-600 text-sm mt-1">{stats.pendingCount} records awaiting action</p>
          </div>
        </div>

        {records.pending.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-dashed border-blue-200">
            <CheckCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <p className="text-slate-700 font-bold text-lg">All Caught Up!</p>
            <p className="text-slate-600">No pending records at the moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.pending.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between p-5 bg-white border border-slate-100 rounded-xl hover:shadow-md hover:border-slate-200 transition"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BarcodeIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-base font-bold text-slate-900">{record.barcodeNumber}</p>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">Created {record.createdAt ? new Date(record.createdAt).toLocaleString() : 'N/A'}</p>
                  <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 w-fit ${getStatusColor(record.status)}`}>
                    {getStatusIcon(record.status)}
                    {record.status}
                  </span>
                </div>
                <button 
                  onClick={() => navigate(`/scan-update?recordId=${record.id}`)}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-bold transition shadow-md"
                >
                  Process
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hold Records */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 hover:shadow-md transition">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">On Hold Records</h3>
            <p className="text-slate-600 text-sm mt-1">{records.hold.length} records awaiting resolution</p>
          </div>
        </div>

        {records.hold.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-dashed border-amber-200">
            <Clock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <p className="text-slate-700 font-bold text-lg">No Records on Hold</p>
            <p className="text-slate-600">All held records have been resolved</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.hold.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between p-5 bg-white border border-amber-100 rounded-xl hover:shadow-md hover:border-amber-200 transition"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-base font-bold text-slate-900">{record.barcodeNumber}</p>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">Created {record.createdAt ? new Date(record.createdAt).toLocaleString() : 'N/A'}</p>
                  <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 w-fit ${getStatusColor(record.status)}`}>
                    {getStatusIcon(record.status)}
                    {record.status}
                  </span>
                </div>
                <button 
                  onClick={() => navigate(`/scan-update?recordId=${record.id}`)}
                  className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white rounded-xl font-bold transition shadow-md"
                >
                  Process
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 hover:shadow-md transition">
        <div className="flex gap-4">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl">
            <AlertIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-indigo-900 mb-4">Service Status Guide</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-xl border border-blue-200">
                <p className="font-bold text-slate-900 mb-2">PENDING</p>
                <p className="text-sm text-slate-700">New records awaiting initial service action</p>
              </div>
              <div className="p-4 bg-white rounded-xl border border-amber-200">
                <p className="font-bold text-slate-900 mb-2">HOLD</p>
                <p className="text-sm text-slate-700">Service in progress, requires additional parts or time</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEngineerDashboard;
